interface IKeyedCacheEntry<T> {
  value: T
  expiresAt: number | null
}

export interface IKeyedRequestCache<T> {
  get(key: string, loader: () => Promise<T>): Promise<T>
  invalidate(key: string): void
  invalidateAll(): void
}

export function createKeyedRequestCache<T>(options?: {ttlMs?: number}): IKeyedRequestCache<T> {
  const ttlMs = options?.ttlMs ?? null
  const resultByKey = new Map<string, IKeyedCacheEntry<T>>()
  const inFlightByKey = new Map<string, Promise<T>>()

  function isFresh(entry: IKeyedCacheEntry<T> | undefined): entry is IKeyedCacheEntry<T> {
    if(!entry) {
      return false
    }
    if(entry.expiresAt === null) {
      return true
    }
    return entry.expiresAt > Date.now()
  }

  async function loadAndCache(key: string, loader: () => Promise<T>): Promise<T> {
    try {
      const value = await loader()
      resultByKey.set(key, {
        value,
        expiresAt: ttlMs === null ? null : Date.now() + ttlMs,
      })
      return value
    } finally {
      inFlightByKey.delete(key)
    }
  }

  return {
    get(key: string, loader: () => Promise<T>): Promise<T> {
      const cached = resultByKey.get(key)
      if(isFresh(cached)) {
        return Promise.resolve(cached.value)
      }
      const inFlight = inFlightByKey.get(key)
      if(inFlight) {
        return inFlight
      }
      const promise = loadAndCache(key, loader)
      inFlightByKey.set(key, promise)
      return promise
    },
    invalidate(key: string): void {
      resultByKey.delete(key)
      inFlightByKey.delete(key)
    },
    invalidateAll(): void {
      resultByKey.clear()
      inFlightByKey.clear()
    },
  }
}
